import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

// Calculate the beginning of the next hour
const now = new Date()
const nextHour = new Date(now)
nextHour.setHours(now.getHours() + 1)
nextHour.setMinutes(0)
nextHour.setSeconds(0)
nextHour.setMilliseconds(0)
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useLazyQuery } from "@apollo/client"
import { useViewer } from "~/auth/use-viewer"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Form } from "~/ui/form"
import { Button } from "~/ui/button"
import { TextField } from "~/fields/text-field"
import { DateTimePicker } from "~/fields/date-time-picker"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/select"
import { Checkbox } from "~/ui/checkbox"
import { Label } from "~/ui/label"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useValidationErrors } from "~/common/use-validation-errors"
import { useToast } from "~/ui/use-toast"
import { NEARBY_LOCATIONS_QUERY } from "./nearby-locations-query"
import { GAME_GENRES, GAME_INTENSITIES } from "~/types/game-preferences"
import { gql } from "~/__generated__"
import { plansPath } from "~/common/paths"
import { GameGenre, GameIntensity } from "~/__generated__/graphql"

const CREATE_PLAN_MUTATION = gql(/* GraphQL */ `
  mutation CreatePlan($input: PlanCreateInput!) {
    planCreate(input: $input) {
      plan {
        id
        title
      }
    }
  }
`)

const formSchema = z.object({
  description: z.string().optional(),
  datetime: z.date(),
  locationId: z.string().min(1, "Location is required"),
  minPeople: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z.number().min(1).optional()
  ),
  maxPeople: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z.number().min(1).optional()
  ),
  genrePreferences: z.array(z.string()),
  intensityPreferences: z.array(z.string()),
})

type FormValues = z.infer<typeof formSchema>

export const PlanCreateScreen = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { viewer } = useViewer()
  const [selectedGenres, setSelectedGenres] = useState<string[]>([])
  const [selectedIntensities, setSelectedIntensities] = useState<string[]>([])

  const [getNearbyLocations, { data: locationsData, loading: locationsLoading }] =
    useLazyQuery(NEARBY_LOCATIONS_QUERY)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: "",
      datetime: nextHour,
      locationId: "",
      minPeople: 2,
      maxPeople: 4,
      genrePreferences: [],
      intensityPreferences: [],
    },
  })

  const [createPlan, mutationResult] = useSafeMutation(CREATE_PLAN_MUTATION)
  useValidationErrors(form.setError, mutationResult)

  // Fetch nearby locations when component mounts
  useEffect(() => {
    if (viewer?.latitude && viewer?.longitude) {
      getNearbyLocations({
        variables: {
          latitude: viewer.latitude,
          longitude: viewer.longitude,
          distance: 25, // 25 miles radius
        },
      })
    }
  }, [viewer?.latitude, viewer?.longitude, getNearbyLocations])

  // Generate title based on location and game preferences
  const generateTitle = (locationId: string): string => {
    const location =
      locationsData?.nearbyLocations?.edges?.find((edge) => edge?.node?.id === locationId)?.node
        ?.name || "Game session"

    // Get selected genres or "Any genre" if all or none selected
    const genreText =
      selectedGenres.length === 0 ||
      selectedGenres.includes("any") ||
      selectedGenres.length === Object.keys(GAME_GENRES).length - 1
        ? "Any genre"
        : selectedGenres.map((g) => GAME_GENRES[g as keyof typeof GAME_GENRES]?.label).join(", ")

    // Get selected intensities or "Any intensity" if all or none selected
    const intensityText =
      selectedIntensities.length === 0 ||
      selectedIntensities.includes("any") ||
      selectedIntensities.length === Object.keys(GAME_INTENSITIES).length - 1
        ? "Any intensity"
        : selectedIntensities
            .map((i) => GAME_INTENSITIES[i as keyof typeof GAME_INTENSITIES]?.label)
            .join(", ")

    return `${genreText} games at ${location} (${intensityText})`
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const title = generateTitle(values.locationId)

      const result = await createPlan({
        variables: {
          input: {
            title: title,
            description: values.description || "",
            datetime: values.datetime.toISOString(),
            locationId: values.locationId,
            minPeople: values.minPeople,
            maxPeople: values.maxPeople,
            genrePreferences: selectedGenres.filter((g) => g !== "any") as GameGenre[],
            intensityPreferences: selectedIntensities.filter((i) => i !== "any") as GameIntensity[],
          },
        },
      })

      if (result.data?.planCreate?.plan) {
        toast({
          title: "Plan Created",
          description: "Your plan has been successfully created.",
        })
        navigate(plansPath({}))
      }
    } catch (error) {
      console.error("Error creating plan:", error)
    }
  }

  const handleGenreChange = (genreId: string, checked: boolean) => {
    if (genreId === "any") {
      if (checked) {
        // Select all genres
        const allGenres = Object.values(GAME_GENRES).map((genre) => genre.id)
        setSelectedGenres(["any", ...allGenres])
      } else {
        // Deselect all genres
        setSelectedGenres([])
      }
    } else {
      if (checked) {
        setSelectedGenres([...selectedGenres.filter((id) => id !== "any"), genreId])
      } else {
        // Remove "any" if it was selected
        const newGenres = selectedGenres.filter((id) => id !== genreId && id !== "any")
        setSelectedGenres(newGenres)
      }
    }
  }

  const handleIntensityChange = (intensityId: string, checked: boolean) => {
    if (intensityId === "any") {
      if (checked) {
        // Select all intensities
        const allIntensities = Object.values(GAME_INTENSITIES).map((intensity) => intensity.id)
        setSelectedIntensities(["any", ...allIntensities])
      } else {
        // Deselect all intensities
        setSelectedIntensities([])
      }
    } else {
      if (checked) {
        setSelectedIntensities([...selectedIntensities.filter((id) => id !== "any"), intensityId])
      } else {
        // Remove "any" if it was selected
        const newIntensities = selectedIntensities.filter(
          (id) => id !== intensityId && id !== "any"
        )
        setSelectedIntensities(newIntensities)
      }
    }
  }

  return (
    <TablePageLayout>
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Create New Plan</h1>
          <p className="text-gray-600">Schedule a new game plan at a location near you</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="locationId">Location</Label>
                <Select
                  onValueChange={(value) => form.setValue("locationId", value)}
                  defaultValue={form.getValues("locationId")}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locationsLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading locations...
                      </SelectItem>
                    ) : locationsData?.nearbyLocations?.edges?.length ? (
                      locationsData.nearbyLocations.edges.map(
                        (edge) =>
                          edge?.node && (
                            <SelectItem key={edge.node.id} value={edge.node.id}>
                              {edge.node.name} ({edge.node.distance?.toFixed(1)} miles)
                            </SelectItem>
                          )
                      )
                    ) : (
                      <SelectItem value="none" disabled>
                        No locations found nearby
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {form.formState.errors.locationId && (
                  <p className="mt-1 text-sm text-red-500">
                    {form.formState.errors.locationId.message}
                  </p>
                )}
              </div>

              <DateTimePicker control={form.control} name="datetime" label="Date & Time" required />

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  control={form.control}
                  name="minPeople"
                  label="Min People"
                  type="number"
                  min={1}
                />
                <TextField
                  control={form.control}
                  name="maxPeople"
                  label="Max People"
                  type="number"
                  min={1}
                />
              </div>

              <TextField
                control={form.control}
                name="description"
                label="Description"
                placeholder="Describe your plan (optional)"
                multiline
                rows={3}
              />
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <h3 className="mb-2 text-lg font-medium">Game Genres</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    key="any-genre"
                    className="col-span-2 flex items-center space-x-2 border-b border-gray-200 pb-2"
                  >
                    <Checkbox
                      id="genre-any"
                      checked={selectedGenres.includes("any")}
                      onCheckedChange={(checked) => handleGenreChange("any", checked as boolean)}
                    />
                    <Label htmlFor="genre-any" className="text-sm font-medium">
                      Any Genre (Select All)
                    </Label>
                  </div>
                  {Object.values(GAME_GENRES).map((genre) => (
                    <div key={genre.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`genre-${genre.id}`}
                        checked={selectedGenres.includes(genre.id)}
                        onCheckedChange={(checked) =>
                          handleGenreChange(genre.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`genre-${genre.id}`} className="text-sm">
                        {genre.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-lg font-medium">Game Intensity</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    key="any-intensity"
                    className="col-span-2 flex items-center space-x-2 border-b border-gray-200 pb-2"
                  >
                    <Checkbox
                      id="intensity-any"
                      checked={selectedIntensities.includes("any")}
                      onCheckedChange={(checked) =>
                        handleIntensityChange("any", checked as boolean)
                      }
                    />
                    <Label htmlFor="intensity-any" className="text-sm font-medium">
                      Any Intensity (Select All)
                    </Label>
                  </div>
                  {Object.values(GAME_INTENSITIES).map((intensity) => (
                    <div key={intensity.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`intensity-${intensity.id}`}
                        checked={selectedIntensities.includes(intensity.id)}
                        onCheckedChange={(checked) =>
                          handleIntensityChange(intensity.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`intensity-${intensity.id}`} className="text-sm">
                        {intensity.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <Button type="submit" disabled={form.formState.isSubmitting || locationsLoading}>
                Create Plan
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(plansPath({}))}>
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </TablePageLayout>
  )
}
